/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { Flag } from 'ui/atoms/Flag/Flag';

import styles from './TaxIdValidationHeroCard.module.scss';

type CountryKey = 'us' | 'gb' | 'de';

interface RecordRow {
  label: string;
  value: string;
  mono?: boolean;
}

interface CountryPanel {
  key: CountryKey;
  flag: string;
  number: string;
  authority: string;
  rows: RecordRow[];
}

const PANELS: CountryPanel[] = [
  {
    key: 'us',
    flag: 'us',
    number: '47-1234567',
    authority: 'IRS · Delaware',
    rows: [
      { label: 'Legal name', value: 'Acme Industries, Inc.' },
      { label: 'Registered address', value: '340 Madison Avenue, New York, NY 10173' },
      { label: 'Incorporated', value: '4 June 2014' },
      { label: 'Legal form', value: 'Corporation (Delaware)' },
      { label: 'Status', value: 'Good standing' },
      { label: 'State file number', value: '7412996', mono: true },
      { label: 'EIN', value: '47-1234567', mono: true },
      { label: 'Ultimate parent', value: 'Acme Holdings plc — United Kingdom' },
    ],
  },
  {
    key: 'gb',
    flag: 'gb',
    number: 'GB123456789',
    authority: 'HMRC · Companies House',
    rows: [
      { label: 'Legal name', value: 'Acme Trading (UK) Limited' },
      { label: 'Registered address', value: '71 Fenchurch Street, London EC3M 4BS' },
      { label: 'Incorporated', value: '12 March 2009' },
      { label: 'Legal form', value: 'Private limited company' },
      { label: 'Status', value: 'Active' },
      { label: 'Company number', value: '06843291', mono: true },
      { label: 'Persons with control', value: 'Acme Holdings plc — 75% or more' },
      { label: 'Officers', value: '4 directors, 1 secretary' },
    ],
  },
  {
    key: 'de',
    flag: 'de',
    number: 'DE811234567',
    authority: 'BZSt · Handelsregister',
    rows: [
      { label: 'Legal name', value: 'Acme Deutschland GmbH' },
      { label: 'Registered office', value: 'Kurfürstendamm 194, 10707 Berlin' },
      { label: 'Incorporated', value: '18 August 2011' },
      { label: 'Legal form', value: 'Gesellschaft mit beschränkter Haftung' },
      { label: 'Status', value: 'Active' },
      { label: 'Register entry', value: 'HRB 128374 B', mono: true },
      { label: 'Share capital', value: 'EUR 250,000' },
      { label: 'Ultimate parent', value: 'Acme Holdings plc — United Kingdom' },
    ],
  },
];

const AUTO_INTERVAL = 3200;

export const TaxIdValidationHeroCard = () => {
  const cardRef = useRef<HTMLDivElement>(null);
  const inView = useIntersection(cardRef, { continuous: true, threshold: 0.3 });

  const [activeIndex, setActiveIndex] = useState(0);
  const [interacted, setInteracted] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const selectPanel = useCallback((index: number, byUser = false) => {
    if (byUser) setInteracted(true);
    setActiveIndex(index);
  }, []);

  useEffect(() => {
    if (!inView || interacted) {
      return;
    }

    timerRef.current = setTimeout(() => {
      setActiveIndex((current) => (current + 1) % PANELS.length);
    }, AUTO_INTERVAL);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [inView, interacted, activeIndex]);

  const active = PANELS[activeIndex];

  return (
    <div className={styles.card} ref={cardRef}>
      <div className={styles.progress}>
        <i
          key={`${active.key}-${interacted}`}
          className={cn(styles.progressFill, !interacted && inView && styles.progressRun)}
        />
      </div>

      <div className={styles.switcher}>
        {PANELS.map((panel, index) => (
          <button
            key={panel.key}
            type="button"
            className={cn(styles.switchBtn, index === activeIndex && styles.switchBtnActive)}
            onClick={() => selectPanel(index, true)}
          >
            <Flag code={panel.flag} className={styles.switchFlag} rounded />
            {panel.number}
          </button>
        ))}
      </div>

      <div className={styles.body}>
        <div className={styles.inputLabel}>Enter an EIN or VAT number</div>

        <div className={styles.inputRow}>
          <Flag code={active.flag} className={styles.inputFlag} rounded />
          <span className={styles.inputNumber}>{active.number}</span>
          <span className={styles.goBtn}>Validate</span>
        </div>

        <div className={styles.recordBlock}>
          <div className={styles.recordHead}>
            <span>Company record</span>
            <span>{active.authority}</span>
          </div>

          {active.rows.map((row, i) => (
            <div
              key={row.label}
              className={cn(styles.recordRow, inView && styles.recordRowIn)}
              style={{ '--delay': `${i * 0.05}s` } as React.CSSProperties}
            >
              <div className={styles.recordKey}>{row.label}</div>
              <div className={cn(styles.recordValue, row.mono && styles.recordValueMono)}>{row.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
